import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { ComponentType, Suspense, lazy, useCallback, useMemo } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { goodFormatNumber } from '../utils';
import { Spinner } from './Spinner';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

export interface ChartBarData {
  date: string | number | Date;
  value: number;
}

export interface ChartBarWithPositiveNegativeProps {
  data: ChartBarData[];
  description?: string;
  height?: number | string;
  colorPositive?: string;
  colorNegative?: string;
  currency?: string;
}

export const ChartBarWithPositiveNegative = ({
  colorPositive = '#16A34A',
  colorNegative = '#DC2626',
  description = '',
  height = '100%',
  data,
  currency = '%',
}: ChartBarWithPositiveNegativeProps) => {
  const fmtPct = useCallback(
    (v: number) => `${v <= 0 ? '' : '+'}${goodFormatNumber(v)}${currency}`,
    [currency],
  );

  const options = useMemo<ApexOptions>(() => {
    const vals = data.map(d => d.value);
    const min = Math.min(...vals, 0);
    const max = Math.max(...vals, 0);

    const absMax = Math.max(Math.abs(min), Math.abs(max));
    const minY = -absMax;
    const maxY = absMax;

    const dates = data.map(item => new Date(item.date).getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);

    return {
      chart: {
        type: 'bar',
        height,
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 400,
        },
        selection: { enabled: false },
        states: {
          normal: { filter: { type: 'none', value: 0 } },
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } },
        },
        zoom: {
          enabled: false,
        },
      },

      plotOptions: {
        bar: {
          columnWidth: '16px',
          borderRadius: 2,
          distributed: false,
          colors: {
            ranges: [
              { from: minY, to: -0.0000001, color: colorNegative },
              { from: 0, to: maxY, color: colorPositive },
            ],
          },
        },
      },

      dataLabels: {
        enabled: false,
      },

      tooltip: {
        shared: false,
        y: {
          formatter: (val: number) => fmtPct(val),
        },
        x: {
          formatter: (val: number) => dayjs(val).format('MMM D, YYYY'),
        },
      },

      xaxis: {
        type: 'datetime',
        min: minDate,
        max: maxDate,
        labels: {
          format: 'MMM yy',
          datetimeUTC: true,
          showDuplicates: false,
          rotate: 0,
          hideOverlappingLabels: false,
          trim: true,
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
            cssClass: '!font-normal',
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },

      yaxis: {
        min: minY,
        max: maxY,
        tickAmount: 4,
        labels: {
          formatter: fmtPct,
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
          },
        },
        forceNiceScale: false,
      },

      grid: {
        borderColor: 'rgba(0,0,0,0.08)',
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },

      // annotations: {
      //   yaxis: [
      //     {
      //       y: 0,
      //       borderColor: '#999',
      //       strokeDashArray: 3,
      //       label: {
      //         text: '',
      //         style: {
      //           color: '#666',
      //           background: '#fff',
      //           fontSize: '10px',
      //         },
      //       },
      //     },
      //   ],
      // },

      stroke: {
        show: false,
      },

      colors: [colorPositive],
    };
  }, [height, colorPositive, colorNegative, data, fmtPct]);

  const series = useMemo(() => {
    const seriesData = data.map(d => ({
      x: typeof d.date === 'number' ? d.date : dayjs(d.date).valueOf(),
      y: d.value,
    }));

    return [
      {
        name: description,
        data: seriesData,
      },
    ];
  }, [data, description]);

  return (
    <Suspense
      fallback={
        <div className="min-h-[250px] h-full w-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Chart type="bar" options={options} series={series} height={height} />
    </Suspense>
  );
};
